import { Fragment, ReactNode, useCallback, useState } from 'react';

import { Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FilterCategoryItem } from '@/components/data-table/filters-v2/data-table-filter-category-item';
import { Button } from '@/components/ui/button';
import { CommandItem, CommandSeparator } from '@/components/ui/command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import { FilterDropdownProps, TableFilterConfig } from './types/types';

/**
 * Main filter dropdown component with filter categories
 */
export function FilterDropdown({
  filtersConfig,
  activeFilters,
  onFilterChange,
  onClearFilter,
  savedFilters = [],
  onApplySavedFilter,
  open: controlledOpen,
  onOpenChange,
  focusedFilterName,
  onFocusedFilterHandled,
}: FilterDropdownProps) {
  const { t } = useTranslation('common');
  const [internalOpen, setInternalOpen] = useState(false);

  // Single "which submenu is open" value. Only one submenu may be open at a time —
  // keeping this in the parent (rather than a per-item useState) guarantees opening
  // one closes the others, even when Radix misses the pointer/focus events that would
  // normally close a sibling (e.g. when switching browser tabs and returning).
  const [openSubmenuName, setOpenSubmenuName] = useState<string | null>(null);

  // Support both controlled and uncontrolled open state
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    setInternalOpen(next);
    onOpenChange?.(next);
    // Collapse any open submenu when the whole dropdown closes
    if (!next) {
      setOpenSubmenuName(null);
    }
  };

  const handleSubmenuOpenChange = useCallback((name: string, next: boolean) => {
    setOpenSubmenuName(prev => {
      if (next) return name;
      // Only clear if this submenu is the one currently marked open
      return prev === name ? null : prev;
    });
  }, []);

  // Filter out search type configs (they're rendered separately)
  const dropdownConfigs = filtersConfig.filter(c => c.type !== 'search');

  // Bucket consecutive configs into sections by their `group`. Ungrouped configs
  // form an anonymous section (no header, no connecting line).
  const sections: Array<{ group?: string; groupIcon?: ReactNode; configs: TableFilterConfig[] }> =
    [];
  for (const config of dropdownConfigs) {
    const last = sections[sections.length - 1];
    if (last && last.group === config.group) {
      last.configs.push(config);
    } else {
      sections.push({ group: config.group, groupIcon: config.groupIcon, configs: [config] });
    }
  }

  // Count total active filters — scoped to configs, excluding search and
  // hidden-from-summary switches (e.g. "Hide archived").
  const totalActiveCount = filtersConfig.reduce((count, config) => {
    if (config.type === 'search') return count;
    if (config.type === 'switch' && config.hiddenFromSummary) return count;

    const value = activeFilters[config.name];
    if (value == null) return count;
    if (Array.isArray(value)) return count + (value.length > 0 ? 1 : 0);
    return count + (value.trim() !== '' ? 1 : 0);
  }, 0);

  const getActiveValues = useCallback(
    (name: string): string[] => {
      const value = activeFilters[name];
      if (!value) return [];
      return Array.isArray(value) ? value : [value];
    },
    [activeFilters]
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-2">
          <Filter className="h-4 w-4" />
          <span>{t('tableFilters.filter')}</span>
          {totalActiveCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-xs font-medium text-background">
              {totalActiveCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        // Cap the height to the space available below the trigger and scroll when
        // the viewport is short (e.g. small laptops / a vertically-resized window).
        className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-56 overflow-y-auto"
      >
        {/* Saved filters section */}
        {savedFilters.length > 0 && onApplySavedFilter && (
          <>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex w-full cursor-pointer items-center justify-between gap-2">
                <span>{t('tableFilters.saved')}</span>
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <span className="text-xs text-muted-foreground">({savedFilters.length})</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="min-w-[180px]">
                {savedFilters.map(filter => (
                  <CommandItem
                    key={filter.id}
                    onSelect={() => {
                      onApplySavedFilter(filter);
                      setOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    {filter.name}
                  </CommandItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <CommandSeparator className="my-1" />
          </>
        )}

        {/* Filter categories, grouped into sections with an icon header and a
            left vertical line connecting the section's items. */}
        {sections.map((section, sectionIndex) => {
          const items = section.configs.map(config => (
            <FilterCategoryItem
              key={config.name}
              config={config}
              activeValues={getActiveValues(config.name)}
              onFilterChange={onFilterChange}
              onClearFilter={onClearFilter}
              activeFilters={activeFilters}
              forceOpen={focusedFilterName === config.name}
              onForceOpenHandled={onFocusedFilterHandled}
              open={openSubmenuName === config.name}
              onOpenChange={next => handleSubmenuOpenChange(config.name, next)}
            />
          ));

          // Ungrouped section: render items plainly, no header or line.
          if (!section.group) {
            return <Fragment key={`section-${sectionIndex}`}>{items}</Fragment>;
          }

          return (
            <div key={section.group} className={cn(sectionIndex > 0 && 'mt-1')}>
              <DropdownMenuLabel className="flex items-center gap-2 px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {section.groupIcon && (
                  <span className="flex h-3.5 w-3.5 items-center justify-center [&_svg]:h-3.5 [&_svg]:w-3.5">
                    {section.groupIcon}
                  </span>
                )}
                {section.group}
              </DropdownMenuLabel>
              {/* Indented items with a connecting vertical line on the left */}
              <div className="ml-[15px] border-l border-border pl-1">{items}</div>
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
